/**
 * Gestionnaire d'erreurs intelligent
 * Détermine si une erreur doit être montrée à l'utilisateur ou juste loggée
 */
import i18n from "../lang/i18n";

// Erreurs à NE PAS montrer à l'utilisateur (problèmes techniques)
const TECHNICAL_ERRORS = [
  /unauthorized/i,
  /401/,
  /403/,
  /network error/i,
  /timeout/i,
  /cors/i,
  /failed to fetch/i,
  /réessai en cours/i,
  /échec après.*réessais/i,
  /resetcaptcha/i,
  /cannot read properties of/i,
  /undefined/i,
  /null/i
];

// Erreurs à montrer à l'utilisateur (problèmes business)
const USER_RELEVANT_ERRORS = [
  /insufficient.*balance/i,
  /solde insuffisant/i,
  /email.*already.*exists/i,
  /mot de passe.*incorrect/i,
  /password.*incorrect/i,
  /compte.*non.*trouvé/i,
  /account.*not.*found/i,
  /service.*non.*disponible/i,
  /service.*unavailable/i,
  /numéro.*non.*disponible/i,
  /number.*unavailable/i,
  /captcha.*incorrect/i,
  /code.*verification.*incorrect/i
];

/**
 * Détermine si une erreur doit être montrée à l'utilisateur
 * @param {string|Error} error - L'erreur à analyser
 * @returns {boolean} - true si l'erreur doit être montrée
 */
export const shouldShowToUser = (error) => {
  const errorMessage = typeof error === 'string' ? error : error?.message || '';
  
  // Vérifier si c'est une erreur technique (à cacher)
  if (TECHNICAL_ERRORS.some(pattern => pattern.test(errorMessage))) {
    return false;
  }
  
  // Vérifier si c'est une erreur business (à montrer)
  if (USER_RELEVANT_ERRORS.some(pattern => pattern.test(errorMessage))) {
    return true;
  }
  
  // Par défaut, ne pas montrer les erreurs non classifiées
  return false;
};

/**
 * Traite une erreur selon son type
 * @param {string|Error} error - L'erreur à traiter
 * @param {function} setShowErrorModal - Fonction pour afficher la modal d'erreur
 * @param {function} setErrorMsg - Fonction pour définir le message d'erreur
 */
export const handleError = (error, setShowErrorModal, setErrorMsg) => {
  // Toujours logger l'erreur pour le debugging
  console.error('Error occurred:', error);
  
  // Ne montrer à l'utilisateur que si c'est pertinent, et jamais le texte brut
  // (anglais ou français selon la source) : un message traduit dans la langue du site.
  if (shouldShowToUser(error)) {
    setShowErrorModal(true);
    setErrorMsg([getFriendlyMessage(error)]);
  }
};

/**
 * Messages d'erreur conviviaux pour l'utilisateur : clés i18n
 * (public/locales/<langue>/translation.json), traduites à l'affichage.
 */
export const USER_FRIENDLY_MESSAGES = {
  NETWORK_ERROR: "signal.err-network",
  UNAUTHORIZED: "signal.err-session",
  INSUFFICIENT_BALANCE: "signal.t-no-balance-body",
  SERVICE_UNAVAILABLE: "signal.err-unavailable",
  NUMBER_UNAVAILABLE: "signal.t-no-number-body",
  GENERIC_ERROR: "signal.t-try-again"
};

/**
 * Transforme une erreur technique en message convivial
 * @param {string|Error} error - L'erreur à transformer
 * @returns {string} - Message convivial
 */
export const getFriendlyMessage = (error) => {
  const errorMessage = typeof error === 'string' ? error : error?.message || '';
  const tr = (key) => i18n.t(key);
  
  if (/network error|failed to fetch|cors/i.test(errorMessage)) {
    return tr(USER_FRIENDLY_MESSAGES.NETWORK_ERROR);
  }
  
  if (/unauthorized|401/i.test(errorMessage)) {
    return tr(USER_FRIENDLY_MESSAGES.UNAUTHORIZED);
  }
  
  if (/insufficient.*(balance|funds)|solde insuffisant/i.test(errorMessage)) {
    return tr(USER_FRIENDLY_MESSAGES.INSUFFICIENT_BALANCE);
  }
  
  if (/(numéro|number).*(non.*disponible|unavailable)/i.test(errorMessage)) {
    return tr(USER_FRIENDLY_MESSAGES.NUMBER_UNAVAILABLE);
  }
  
  if (/service.*unavailable|service.*non.*disponible/i.test(errorMessage)) {
    return tr(USER_FRIENDLY_MESSAGES.SERVICE_UNAVAILABLE);
  }
  
  return tr(USER_FRIENDLY_MESSAGES.GENERIC_ERROR);
};

